import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor'
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper'
import vtkTexture from '@kitware/vtk.js/Rendering/Core/Texture'
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray'

import ImageHelper from '@kitware/vtk.js/Common/Core/ImageHelper'

import vtkAppendPolyData from '@kitware/vtk.js/Filters/General/AppendPolyData'
import vtkCylinderSource from '@kitware/vtk.js/Filters/Sources/CylinderSource'
import vtkSphereSource from '@kitware/vtk.js/Filters/Sources/SphereSource'
import vtkPlaneSource from '@kitware/vtk.js/Filters/Sources/PlaneSource'

import brandmark from '../../assets/pollination_brandmark.png'
import logo from '../../assets/pollination_logo_120.png'

// blender / threejs like orientation indicator
export function orientationIndicator() {
  const axisRadius = 0.045
  const sphereRadius = 0.25
  const sphereTheta = 10
  const spherePhi = 10

  function addColor(ds, r, g, b) {
    const size = ds.getPoints().getData().length
    const rgbArray = new Uint8Array(size)
    let offset = 0
  
    while (offset < size) {
      rgbArray[offset++] = r
      rgbArray[offset++] = g
      rgbArray[offset++] = b
    }
  
    ds.getPointData().setScalars(
      vtkDataArray.newInstance({
        name: 'color',
        numberOfComponents: 3,
        values: rgbArray,
      })
    )
  }
  
  const xAxisSource = vtkAppendPolyData.newInstance()
  xAxisSource.setInputData(vtkCylinderSource.newInstance({
    radius: axisRadius,
    resolution: 20,
    direction: [1.0, 0.0, 0.0],
    center: [0.5, 0.0, 0.0],
  }).getOutputData())
  xAxisSource.addInputData(vtkSphereSource.newInstance({
    radius: sphereRadius,
    center: [1.0, 0.0, 0.0],
    thetaResolution: sphereTheta,
    phiResolution: spherePhi,
  }).getOutputData())
  xAxisSource.addInputData(vtkSphereSource.newInstance({
    radius: sphereRadius,
    center: [-1.0, 0.0, 0.0],
    thetaResolution: sphereTheta,
    phiResolution: spherePhi,
  }).getOutputData())
  const xAxis = xAxisSource.getOutputData()
  addColor(xAxis, 94, 252, 20)

  const yAxisSource = vtkAppendPolyData.newInstance()
  yAxisSource.setInputData(vtkCylinderSource.newInstance({
    radius: axisRadius,
    resolution: 20,
    direction: [0.0, 1.0, 0.0],
    center: [0.0, 0.5, 0.0],
  }).getOutputData())
  yAxisSource.addInputData(vtkSphereSource.newInstance({
    radius: sphereRadius,
    center: [0.0, 1.0, 0.0],
    thetaResolution: sphereTheta,
    phiResolution: spherePhi,
  }).getOutputData())
  yAxisSource.addInputData(vtkSphereSource.newInstance({
    radius: sphereRadius,
    center: [0.0, -1.0, 0.0],
    thetaResolution: sphereTheta,
    phiResolution: spherePhi,
  }).getOutputData())

  const yAxis = yAxisSource.getOutputData()
  addColor(yAxis, 46, 143, 252)

  const zAxisSource = vtkAppendPolyData.newInstance()
  zAxisSource.setInputData(vtkCylinderSource.newInstance({
    radius: axisRadius,
    resolution: 20,
    direction: [0.0, 0.0, 1.0],
    center: [0.0, 0.0, 0.5],
  }).getOutputData())
  zAxisSource.addInputData(vtkSphereSource.newInstance({
    radius: sphereRadius,
    center: [0.0, 0.0, 1.0],
    thetaResolution: sphereTheta,
    phiResolution: spherePhi,
  }).getOutputData())
  zAxisSource.addInputData(vtkSphereSource.newInstance({
    radius: sphereRadius,
    center: [0.0, 0.0, -1.0],
    thetaResolution: sphereTheta,
    phiResolution: spherePhi,
  }).getOutputData())

  const zAxis = zAxisSource.getOutputData()
  addColor(zAxis, 241, 59, 74)

  const axesSource = vtkAppendPolyData.newInstance()
  axesSource.setInputData(xAxis)
  axesSource.addInputData(yAxis)
  axesSource.addInputData(zAxis)

  const axesMapper = vtkMapper.newInstance()
  axesMapper.setInputData(axesSource.getOutputData())

  const axes = vtkActor.newInstance()
  axes.setMapper(axesMapper)
  const axesProperty = axes.getProperty()
  axesProperty.setLighting(false)

  return axes
}

export function compassRoseIndicator() {
  
  const dist = 15
  const side = 7

  const northPlane = vtkPlaneSource.newInstance({
    point1: [-side/2, dist + side, 0],
    point2: [side/2, dist, 0],
    origin: [-side/2, dist, 0]
  })

  const canvas = document.createElement('canvas')
  const mapper = vtkMapper.newInstance()
  const texture = vtkTexture.newInstance()
  texture.setInterpolate(true)
  canvas.width = 450
  canvas.height = 450
  const ctxt = canvas.getContext('2d')

  ctxt.fillStyle = '#ffffff00'
  ctxt.fillRect(0,0, canvas.width, canvas.height)
  
  ctxt.save()
  
  ctxt.translate(canvas.width/2, canvas.height/2)
  ctxt.scale(20, 20)
  ctxt.rotate(Math.PI * (90 / 180.0))

  ctxt.fillStyle = '#000000'
  ctxt.strokeStyle = '#000000'
  ctxt.textAlign = 'center'
  ctxt.textBaseline = 'middle'
  ctxt.font = 'bold 3px sans-serif'

  ctxt.lineWidth = 0.25
  ctxt.beginPath()
  ctxt.arc(0, 0, 6.5, 0, 2 * Math.PI)
  ctxt.stroke()
  ctxt.beginPath()
  ctxt.arc(0, 0, 5.5, 0, 2 * Math.PI)
  ctxt.stroke()

  ctxt.beginPath()
  ctxt.moveTo(0, 4.75)
  ctxt.lineTo(0, 8)
  ctxt.moveTo(0, -4.75)
  ctxt.lineTo(0, -8)
  ctxt.moveTo(4.75, 0)
  ctxt.lineTo(8, 0)
  ctxt.moveTo(-4.75, 0)
  ctxt.lineTo(-8, 0)
  ctxt.stroke()

  ctxt.lineWidth = 0.45
  ctxt.beginPath()
  ctxt.arc(0, 0, 4.75, 0, 2 * Math.PI)
  ctxt.stroke()
  
  ctxt.fillText('N', 0, -9.5)
  ctxt.fillText('E', 9.5, 0)
  ctxt.fillText('S', 0, 9.5)
  ctxt.fillText('W', -9.5, 0)

  ctxt.restore()

  const vtkImage = ImageHelper.canvasToImageData(canvas)
  texture.setInputData(vtkImage)

  mapper.setInputData(northPlane.getOutputData())
  const northPlaneActor = vtkActor.newInstance()
  northPlaneActor.setMapper(mapper)
  northPlaneActor.addTexture(texture)

  return northPlaneActor
}

export function brandingActor(render, large=true) {

  const plane = vtkPlaneSource.newInstance({
    point1: [1, 0, 0],
    point2: [0, -1, 0],
    origin: [0, 0, 0],
  })

  const mapper = vtkMapper.newInstance()
  mapper.setInputData(plane.getOutputData())

  const texture = vtkTexture.newInstance()
  texture.setInterpolate(true)
  
  const planeActor = vtkActor.newInstance()

  if (large) {
    planeActor.setScaleFrom([.905, .175, 1])
  }
  
  planeActor.setMapper(mapper)
  planeActor.addTexture(texture)

  const img = new Image()
  
  img.addEventListener('load', () => {
    const vtkImage = ImageHelper.imageToImageData(img)
    texture.setInputData(vtkImage)
    render()
  }, false)
  
  img.src = large ? brandmark : logo

  return planeActor
}